import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  BillingContact,
  billingContactSchema,
} from '@/pages/enterprise/_data/payment-billing-contact-schema'
import { zodResolver } from '@hookform/resolvers/zod'
import { isEmpty } from 'lodash'
import { useForm } from 'react-hook-form'

interface Props {
  billingContact: BillingContact
  billingContacts: BillingContact[]
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmitDone: (billingContact: BillingContact) => void
}

export default function BillingContactEditDialog({
  open,
  billingContact,
  billingContacts,
  onOpenChange,
  onSubmitDone,
}: Props) {
  const form = useForm<BillingContact>({
    resolver: zodResolver(
      billingContactSchema
        .optional()
        .refine(
          (data) => {
            const email = data?.email ?? ''
            return isEmpty(email) || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
          },
          { path: ['email'], message: 'Invalid email' }
        )
        .refine(
          (data) => {
            const email = data?.email ?? ''
            return (
              isEmpty(email) ||
              !billingContacts.some(
                (contact) =>
                  contact.email === email &&
                  contact.email !== billingContact.email
              )
            )
          },
          { path: ['email'], message: 'Email already exists' }
        )
    ),

    defaultValues: {
      lastName: billingContact.lastName,
      firstName: billingContact.firstName,
      email: billingContact.email,
    },
  })

  const onSubmit = (data: BillingContact) => {
    onSubmitDone(data)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <h2 className="text-2xl font-bold ">Edit Billing Contact</h2>
        </DialogHeader>
        <>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div className=" flex flex-col gap-4 2xl:flex-row md:flex-row">
                <div className="flex-1 grid grid-cols-1  2xl:grid-cols-3 md:grid-cols-3 gap-4">
                  <FormField
                    control={form.control}
                    name="firstName"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl className="h-14">
                          <Input
                            placeholder="First name"
                            className="h-14 md:col-span-1"
                            {...field}
                          />
                        </FormControl>

                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="lastName"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Input
                            placeholder="Last name"
                            className="h-14 md:col-span-1"
                            {...field}
                          />
                        </FormControl>

                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Input
                            placeholder="Email"
                            className="h-14 md:col-span-1"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <Button className="w-20 h-14" type="submit">
                  Update
                </Button>
              </div>
            </form>
          </Form>
        </>
      </DialogContent>
    </Dialog>
  )
}
